// src/components/filters/FilterBar.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { HStack, Select, useColorModeValue } from "@chakra-ui/react";
import SearchBar from "../UI/SearchBar";
import { Rubric } from "../../model/rubric";
import { useTranslation } from "react-i18next";
import CustomButton from "../UI/CustomButton";
import { searchRecordingTitles } from "../../services/search";
import { RecordingTitleSearchItem } from "../../model/search";
import { useDebouncedCallback } from "../../hooks/useDebouncedCallback";

export type FilterMode = "date" | "categoryOrRubric";

interface FilterBarProps {
  onSearch: (value: string) => void;
  searchValue?: string;
  placeholder?: string;
  debounceMs?: number;
  rubrics?: Rubric[];
  selectedRubricID?: string;
  onRubricChange?: (rubricID: string) => void;
  rubricSelectId?: string;
  isLoading?: boolean;
  showRubricSelect?: boolean;
  variant?: "standard" | "archive";

  // Archive variant props
  filterMode?: FilterMode;
  onFilterModeChange?: (mode: FilterMode) => void;
  selectedMonth?: string;
  onMonthChange?: (month: string) => void;
  selectedYear?: string;
  onYearChange?: (year: string) => void;
  selectedArchiveRubricID?: string;
  onArchiveRubricChange?: (rubricID: string) => void;
  monthOptions?: Array<{ value: string; label: string }>;
  yearOptions?: Array<{ value: string; label: string }>;
  onApplyFilter?: () => void;
  searchMode?: "local" | "fetch";
  onSuggestionSelect?: (item: RecordingTitleSearchItem) => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  onSearch,
  searchValue,
  placeholder,
  debounceMs = 0,
  rubrics = [],
  selectedRubricID = "",
  onRubricChange,
  rubricSelectId,
  isLoading = false,
  showRubricSelect = true,
  variant = "standard",
  filterMode = "date",
  onFilterModeChange,
  selectedMonth = "",
  onMonthChange,
  selectedYear = "",
  onYearChange,
  selectedArchiveRubricID = "",
  onArchiveRubricChange,
  monthOptions = [],
  yearOptions = [],
  onApplyFilter,
  searchMode = "fetch",
  onSuggestionSelect,
}) => {
  const { t } = useTranslation();
  const [localSearchValue, setLocalSearchValue] = useState(searchValue ?? "");
  const debouncedOnSearch = useDebouncedCallback(onSearch, debounceMs);

  const handleSearchChange = (val: string) => {
    setLocalSearchValue(val);
    debouncedOnSearch(val);
  };

  const modeOptions = useMemo(
    () => [
      { value: "date", label: t("filters.mode.date") },
      {
        value: "categoryOrRubric",
        label: t("filters.mode.categoryOrRubric"),
      },
    ],
    [t],
  );

  const selectBg = useColorModeValue("white", "gray.800");
  const selectColor = useColorModeValue("gray.800", "gray.100");
  const selectHoverBorderColor = useColorModeValue("gray.400", "gray.600");

  return (
    <HStack
      w="full"
      spacing={{ base: 2, md: 3 }}
      mb={7}
      align="stretch"
      flexDirection="row"
    >
      {variant !== "archive" && (
        <SearchBar
          sharedRow={showRubricSelect}
          onSearch={handleSearchChange}
          value={localSearchValue}
          placeholder={placeholder}
          mode={searchMode}
          debounceMs={debounceMs}
          fetchSuggestions={
            searchMode === "fetch" ? searchRecordingTitles : undefined
          }
          onSuggestionSelect={(item) => {
            onSuggestionSelect?.(item);
          }}
        />
      )}

      {variant === "archive" ? (
        <HStack
          spacing={{ base: 2, md: 3 }}
          w="full"
          flexDirection="row"
          flexWrap="nowrap"
          align="stretch"
        >
          <Select
            borderRadius="4px"
            bg={selectBg}
            color={selectColor}
            flex="1.1 1 0"
            minW="0"
            value={filterMode}
            // isDisabled={isLoading}
            onChange={(event) =>
              onFilterModeChange?.(event.target.value as FilterMode)
            }
          >
            {modeOptions.map((modeOption) => (
              <option key={modeOption.value} value={modeOption.value}>
                {modeOption.label.toUpperCase()}
              </option>
            ))}
          </Select>

          {filterMode === "date" ? (
            <>
              <Select
                borderRadius="4px"
                color={selectColor}
                bg={selectBg}
                // fontSize={{ base: "xs", md: "sm" }}
                flex="1 1 0"
                minW="0"
                value={selectedMonth}
                isDisabled={isLoading}
                onChange={(event) => onMonthChange?.(event.target.value)}
              >
                {monthOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label.toUpperCase()}
                  </option>
                ))}
              </Select>

              <Select
                borderRadius="4px"
                color={selectColor}
                bg={selectBg}
                // fontSize={{ base: "xs", md: "sm" }}
                flex="0.9 1 0"
                minW="0"
                value={selectedYear}
                isDisabled={isLoading}
                onChange={(event) => onYearChange?.(event.target.value)}
              >
                {yearOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label.toUpperCase()}
                  </option>
                ))}
              </Select>
            </>
          ) : (
            <>
              <Select
                borderRadius="4px"
                // bg={selectBg}
                color={selectColor}
                bg={selectBg}
                // fontSize={{ base: "xs", md: "sm" }}
                flex="1.9 1 0"
                minW="0"
                value={selectedArchiveRubricID}
                // isDisabled={isLoading}
                onChange={(event) =>
                  onArchiveRubricChange?.(event.target.value)
                }
              >
                {rubrics.map((rubric) => (
                  <option key={rubric.rubricID} value={rubric.rubricID}>
                    {rubric.title.toUpperCase()}
                  </option>
                ))}
              </Select>
            </>
          )}

          <CustomButton
            flex="0.5 1 0"
            minW="0"
            onClick={onApplyFilter}
            isDisabled={isLoading}
            colorScheme="blue"
            h="40px"
            // fontSize={{ base: "xs", md: "sm" }}
            // px={{ base: 2, md: 4 }}
          >
            {t("filters.apply")}
          </CustomButton>
        </HStack>
      ) : (
        showRubricSelect && (
          <Select
            id={rubricSelectId}
            borderRadius="4px"
            bg={selectBg}
            color={selectColor}
            // fontSize={{ base: "xs", md: "sm" }}
            flex="1 1 50%"
            minW="0"
            maxW="50%"
            value={selectedRubricID}
            isDisabled={isLoading}
            onChange={(event) => onRubricChange?.(event.target.value)}
          >
            {rubrics.map((rubric) => (
              <option key={rubric.rubricID} value={rubric.rubricID}>
                {rubric.title.toUpperCase()}
              </option>
            ))}
          </Select>
        )
      )}
    </HStack>
  );
};

export default FilterBar;
